'use client';

import cx from 'classix';
import {
  useAtomValue,
  useSetAtom,
} from 'jotai';

import { listingMapHighlightAtom } from '@/features/listing/atoms/listing-map-highlight-atom';
import ListingCardViewGrid
  from '@/features/listing/components/listing-card/listing-card-view-grid/listing-card-view-grid';
import ListingCardViewList
  from '@/features/listing/components/listing-card/listing-card-view-list/listing-card-view-list';
import { LISTING_VIEW_VARIANTS } from '@/features/listing/constants/listing-constants';
import {
  type ListingCardView,
  type ListingType,
  ListingTypeId,
} from '@/features/listing/types/listing-types';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';

import styles from './listing-card.module.scss';
import { type ListingCardBaseProps } from './listing-card-types';

interface ListingCardProps extends ListingCardBaseProps {
  hideBorder?: boolean;
  listingViewVariant?: ListingCardView;
}

export default function ListingCard(props: ListingCardProps) {
  const {
    hideBorder = false,
    isListingLinkDisabled = false,
    link = null,
    listing,
    listingViewVariant = LISTING_VIEW_VARIANTS.grid,
    messageSrc,
    showBookmarkButton = true,
    showCheckStatus = false,
    showDescription = true,
    showEditButton = false,
    showListingUser = true,
    showMessageButton = true,
    showMissingData = false,
    showMoveInDate = true,
    showVerifications = true,
  } = props;

  const setListingMapHighlight = useSetAtom(listingMapHighlightAtom);
  const loggedUser = useAtomValue(loggedUserAtom);

  const listingType = ListingTypeId[listing.listingTypeId] as ListingType;
  const isListingPerson = listingType === 'needapartment' || listingType === 'needroom';
  const isListingOfLoggedUser = loggedUser?.id === listing.user.id;

  const handleSetListingMapHighlight = () => {
    setListingMapHighlight({
      coordinates: listing.geoLocation,
      id: listing.id,
    });
  };

  const handleRemoveListingMapHighlight = () => {
    setListingMapHighlight(null);
  };

  return (
    <div
      className={cx(styles.listingCard, hideBorder && styles.listingCard__withoutBorder)}
      onMouseEnter={handleSetListingMapHighlight}
      onMouseLeave={handleRemoveListingMapHighlight}
    >
      {listingViewVariant === LISTING_VIEW_VARIANTS.grid && (
        <ListingCardViewGrid
          showDescription={showDescription}
          listing={listing}
          showListingUser={showListingUser}
          showMessageButton={!isListingOfLoggedUser && showMessageButton}
          showMoveInDate={showMoveInDate}
          showVerifications={showVerifications}
          link={link}
          isListingPerson={isListingPerson}
          showMissingData={showMissingData}
          showCheckStatus={showCheckStatus}
          isListingLinkDisabled={isListingLinkDisabled}
          showEditButton={showEditButton}
          messageSrc={messageSrc}
        />
      )}

      {listingViewVariant === LISTING_VIEW_VARIANTS.list && (
        <ListingCardViewList
          showDescription={showDescription}
          listing={listing}
          showListingUser={showListingUser}
          showMessageButton={showMessageButton}
          showMoveInDate={showMoveInDate}
          showVerifications={showVerifications}
          link={link}
          isListingPerson={isListingPerson}
          showMissingData={showMissingData}
          showCheckStatus={showCheckStatus}
          showBookmarkButton={showBookmarkButton}
          isListingLinkDisabled={isListingLinkDisabled}
        />
      )}
    </div>
  );
}
